import React from "react";
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Button,
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";

const ProductInfo = ({
  product,
  isMobile,
  selectedVariant,
  selectedVariantId,
  setSelectedVariantId,
  handleAddToCart,
}) => {
  const computePrices = () => {
    // PRODUCTO SIN VARIANTE SELECCIONADA
    if (!selectedVariant) {
      const originalNet = Number(product?.pvp1 ?? 0);
      const finalNet =
        product?.precio_descuento != null
          ? Number(product.precio_descuento)
          : originalNet;

      const ivaOriginal = Number(product?.iva_valor ?? 0);
      const ivaFinal =
        product?.precio_descuento != null
          ? Number(
              product.iva_aplicado_valor ?? product.iva_valor ?? 0
            )
          : ivaOriginal;

      const ivaPct = Number(
        product.iva_aplicado_porcentaje ?? product.iva_porcentaje ?? 0
      );

      return {
        originalNet,
        finalNet,
        ivaOriginal,
        ivaFinal,
        ivaPct,
      };
    }

    // CUANDO HAY VARIANTE SELECCIONADA
    const originalNet = Number(selectedVariant.price ?? 0);
    const finalNet =
      selectedVariant.precio_descuento != null
        ? Number(selectedVariant.precio_descuento)
        : originalNet;

    const ivaOriginal = Number(selectedVariant.iva_valor ?? 0);
    const ivaFinal =
      selectedVariant.precio_descuento != null
        ? Number(
            selectedVariant.iva_aplicado_valor ??
              selectedVariant.iva_valor ??
              0
          )
        : ivaOriginal;

    const ivaPct = Number(
      selectedVariant.iva_aplicado_porcentaje ??
        selectedVariant.iva_porcentaje ??
        0
    );

    return {
      originalNet,
      finalNet,
      ivaOriginal,
      ivaFinal,
      ivaPct,
    };
  };

  const finalPriceForDisplay = computePrices();

  const priceOriginalWithIva = (
    finalPriceForDisplay.originalNet + finalPriceForDisplay.ivaOriginal
  ).toFixed(2);

  const priceFinalWithIva = (
    finalPriceForDisplay.finalNet + finalPriceForDisplay.ivaFinal
  ).toFixed(2);

  const hasDiscount =
    finalPriceForDisplay.finalNet < finalPriceForDisplay.originalNet - 1e-6;

  const refLine = selectedVariant?.sku
    ? `REF: ${product.code} - SKU: ${selectedVariant.sku}`
    : `REF: ${product.code}`;

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        width: "100%",
        gap: { xs: 0.4, sm: 0.8 },
      }}
      onClick={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <Typography
        variant="subtitle1"
        sx={{
          mt: { xs: 0, sm: 0.5 },
          fontWeight: 500,
          fontSize: { xs: "0.9rem", sm: "1.05rem" },
          color: "#000",
          textAlign: "left",
          lineHeight: 1.2,
          overflow: "hidden",
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          minHeight: { xs: "2.2em", sm: "2.5em" },
        }}
      >
        {product.name}
      </Typography>

      {/* Marca + texto de IVA (solo visual, la data sigue existiendo) */}
      <Box sx={{ mt: { xs: 0.1, sm: 0.3 } }}>
        <Typography
          sx={{
            color: "#555",
            fontSize: { xs: "0.8rem", sm: "0.9rem" },
            textAlign: "left",
          }}
        >
          {product.brand}
        </Typography>
        <Typography
          sx={{
            color: "#777",
            fontSize: { xs: "0.78rem", sm: "0.86rem" },
            textAlign: "left",
          }}
        >
          Precio incluye IVA
        </Typography>
      </Box>

      {/* Referencia (oculta visualmente, pero se mantiene la data para el carrito) */}
      <Box sx={{ display: "none" }}>
        <Typography sx={{ fontSize: "0.75rem" }}>{refLine}</Typography>
      </Box>

      {product?.variants?.length ? (
        <Box sx={{ mt: { xs: 0.4, sm: 0.8 } }}>
          <Typography
            sx={{
              color: "#666",
              fontSize: { xs: "0.78rem", sm: "0.86rem" },
              mb: 0.4,
              textAlign: "left",
            }}
          >
            {product.variants?.[0]?.variant_type}
          </Typography>

          <FormControl size="small" fullWidth>
            <Select
              value={selectedVariantId}
              onChange={(e) => setSelectedVariantId(e.target.value)}
              displayEmpty
              sx={{ height: { xs: 34, sm: 38 }, fontSize: { xs: "0.86rem", sm: "0.9rem" } }}
              renderValue={(sel) =>
                !sel ? (
                  <Typography sx={{ color: "#999", fontSize: { xs: "0.86rem", sm: "0.9rem" } }}>
                    Seleccionar
                  </Typography>
                ) : (
                  product.variants.find((v) => String(v.id) === String(sel))?.value
                )
              }
            >
              <MenuItem value="" disabled>
                Seleccionar
              </MenuItem>
              {product.variants.map((v) => (
                <MenuItem key={v.id} value={v.id}>
                  {v.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedVariant ? (
            <Box sx={{ mt: { xs: 0.3, sm: 0.7 } }}>
              <Box
                sx={{
                  minHeight: { xs: 30, sm: 40 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: 1,
                }}
              >
                {hasDiscount && (
                  <Typography
                    sx={{
                      color: "#9e9e9e",
                      textDecoration: "line-through",
                      fontSize: { xs: "0.9rem", sm: "1rem" },
                    }}
                  >
                    US$ {priceOriginalWithIva}
                  </Typography>
                )}

                <Typography
                  sx={{
                    color: "#1565C0",
                    fontWeight: 700,
                    fontSize: { xs: "1.02rem", sm: "1.18rem" },
                  }}
                >
                  US$ {priceFinalWithIva}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                mt: { xs: 0.3, sm: 0.7 },
                minHeight: { xs: 30, sm: 40 },
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <Typography
                sx={{
                  color: "#999",
                  fontWeight: 600,
                  textAlign: "left",
                  fontSize: { xs: "0.86rem", sm: "0.9rem" },
                }}
              >
                Seleccionar {product.variants?.[0]?.variant_type}
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ mt: { xs: 0.4, sm: 0.8 } }}>
          <Box
            sx={{
              minHeight: { xs: 30, sm: 40 },
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 1,
            }}
          >
            {hasDiscount && (
              <Typography
                sx={{
                  color: "#9e9e9e",
                  textDecoration: "line-through",
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                }}
              >
                US$ {priceOriginalWithIva}
              </Typography>
            )}

            <Typography
              sx={{
                color: "#1565C0",
                fontWeight: 700,
                fontSize: { xs: "1.02rem", sm: "1.18rem" },
              }}
            >
              US$ {priceFinalWithIva}
            </Typography>
          </Box>
        </Box>
      )}

      <Button
        variant="outlined"
        color="primary"
        disabled={product?.variants?.length ? !selectedVariant : false}
        onClick={(e) => {
          e.stopPropagation();
          handleAddToCart(e, {
            iva_porcentaje: finalPriceForDisplay.ivaPct,
            iva_valor: finalPriceForDisplay.ivaFinal,
          });
        }}
        sx={{
          textTransform: "none",
          mt: { xs: 1, sm: 1.6 },
          alignSelf: "center",
          borderColor: "#1976d2",
          color: "#1976d2",
          backgroundColor: "#fff",
          width: { xs: "82%", sm: "85%" },
          height: { xs: 32, sm: 36 },
          fontSize: { xs: "0.85rem", sm: "0.95rem" },
        }}
        startIcon={<ShoppingCartIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
      >
        Agregar
      </Button>
    </Box>
  );
};

export default ProductInfo;
